import { GMXCarousel } from '../../core/carousel/carousel'

/**
 * Tile - Gallery
 */
export class GMXGalleryTile extends HTMLElement {
	galleryOverlayEl: HTMLElement
	galleryOverlayHeadingEl: HTMLElement
	galleryCarouselEl: GMXCarousel
	galleryCarouselSlidesCollectionEl: HTMLElement
	galleryPopPanelBtn: HTMLButtonElement

	private resizeObserver: ResizeObserver

	private boundCarouselSlidesClickHandler = this._handleCarouselSlideCollectionClick.bind(this)
	private boundCarouselSlideChangeHandler = this._handleCarouselSlideChange.bind(this)

	private _isMobileView: boolean
	get isMobileView(): boolean {
		return this._isMobileView
	}
	set isMobileView(val: boolean) {
		this._isMobileView = val
	}

	connectedCallback(): void {
		this.galleryOverlayEl = this.querySelector('.content-overlay-container')
		this.galleryOverlayHeadingEl = this.querySelector('.gallery-overlay-heading')
		this.galleryCarouselEl = this.querySelector('gmx-carousel')
		this.galleryCarouselSlidesCollectionEl = this.galleryCarouselEl?.querySelector('.slides-collection')
		this.galleryPopPanelBtn = this.querySelector('.cta-gallery-pop-detail')

		if (this.galleryCarouselEl) {
			this.initCarousel()

			// Need to check to see if the first slide requires a text inversion
			const firstSlide = this.galleryCarouselEl.querySelector('.slides-collection > .slide')
			if (firstSlide?.classList.contains('text-invert')) {
				this.galleryOverlayEl.classList.add('gmx-invert')
			} else {
				this.galleryOverlayEl.classList.remove('gmx-invert')
			}
		}

		this.resizeObserver = new ResizeObserver(() => {
			this._syncPopDetailBtnWidth()

			if (this.isMobile() !== this.isMobileView) {
				this.initCarousel()
			}
		})
		this.resizeObserver.observe(this)

		if (this.galleryPopPanelBtn) {
			this.resizeObserver.observe(this.galleryPopPanelBtn)
			this._syncPopDetailBtnWidth()
		}
	}

	disconnectedCallback(): void {
		if (this.resizeObserver) {
			this.resizeObserver.unobserve(this)

			if (this.galleryPopPanelBtn) {
				this.resizeObserver.unobserve(this.galleryPopPanelBtn)
			}
		}

		if (this.galleryCarouselEl) {
			this.galleryCarouselEl.removeEventListener('gmxCarouselSlideChange', this.boundCarouselSlideChangeHandler)
		}

		if (this.galleryCarouselSlidesCollectionEl) {
			this.galleryCarouselSlidesCollectionEl.removeEventListener('click', this.boundCarouselSlidesClickHandler)
		}
	}

	/**
	 * Method to handle when the user clicks on the carousel slide collection, which will trigger the gallery panel to open
	 * @private
	 */
	private _handleCarouselSlideCollectionClick(e: Event): void {
		const targetEl = e.target instanceof HTMLElement ? e.target : null
		const clickedToastTrigger = !!(
			targetEl &&
			(targetEl.matches('[data-toast-uri],[data-toast-msg]') ||
				targetEl.closest('[data-toast-uri],[data-toast-msg]'))
		)

		if (this.galleryPopPanelBtn && !clickedToastTrigger) {
			if (typeof this.galleryPopPanelBtn.dispatchEvent === 'function') {
				this.galleryPopPanelBtn.dispatchEvent(
					new MouseEvent('click', {
						bubbles: true,
						cancelable: true,
						view: window,
					})
				)
			} else {
				this.galleryPopPanelBtn.click()
			}
		}
	}

	/**
	 * Method to handle when the child carousel changes slides
	 */
	private _handleCarouselSlideChange(e: CustomEvent): void {
		if (e.detail?.isTextInvert) {
			this.galleryOverlayEl.classList.add('gmx-invert')
		} else {
			this.galleryOverlayEl.classList.remove('gmx-invert')
		}
	}

	initCarousel(): void {
		if (!this.isMobile()) {
			this.isMobileView = false
			this.galleryCarouselEl.classList.add('dots-align-center')
		} else {
			this.isMobileView = true
			this.galleryCarouselEl.classList.remove('dots-align-center')
		}

		this.galleryCarouselEl.removeEventListener('gmxCarouselSlideChange', this.boundCarouselSlideChangeHandler)
		this.galleryCarouselEl.addEventListener('gmxCarouselSlideChange', this.boundCarouselSlideChangeHandler)

		if (this.galleryCarouselSlidesCollectionEl) {
			this.galleryCarouselSlidesCollectionEl.removeEventListener('click', this.boundCarouselSlidesClickHandler)
			this.galleryCarouselSlidesCollectionEl.addEventListener('click', this.boundCarouselSlidesClickHandler)
		}
	}

	/**
	 * Check if the screen is mobile size
	 */
	isMobile(): boolean {
		return !window.matchMedia('screen and (min-width: 900px)').matches
	}

	/**
	 * Sync the gallery-pop-detail button width so carousel controls can sit inset next to it
	 */
	private _syncPopDetailBtnWidth(): void {
		if (!this.galleryPopPanelBtn) {
			return
		}

		this.style.setProperty(
			'--gallery-pop-detail-btn-width',
			`${this.galleryPopPanelBtn.getBoundingClientRect().width}px`
		)
	}
}

if (!customElements.get('gmx-gallery-tile')) {
	customElements.define('gmx-gallery-tile', GMXGalleryTile)
}
